import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Save, Building2, Phone, CheckCircle2,
  Loader2, User, Shield, AlertCircle, Zap,
  Eye, EyeOff, Key, Wifi, WifiOff, RefreshCw,
  ExternalLink, Copy, Check,
} from 'lucide-react';
import { Input }    from '@/components/ui/input';
import { Label }    from '@/components/ui/label';
import api          from '@/lib/api';
import useAuthStore from '@/store/useAuthStore';
import { toast }    from 'sonner';

const fadeUp = (delay = 0) => ({
  initial:    { opacity: 0, y: 16 },
  animate:    { opacity: 1, y: 0  },
  transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1], delay },
});

export default function Settings() {
  const user = useAuthStore(s => s.user);

  // ── Business state ──────────────────────────────────────────────────────
  const [business,   setBusiness]   = useState(null);
  const [formData,   setFormData]   = useState({ name: '', category: '' });
  const [bizLoading, setBizLoading] = useState(false);

  // ── Twilio state ────────────────────────────────────────────────────────
  const [twilioForm,    setTwilioForm]    = useState({
    accountSid: '', authToken: '', whatsappNumber: '',
  });
  const [showToken,     setShowToken]     = useState(false);
  const [twilioLoading, setTwilioLoading] = useState(false);
  const [twilioStatus,  setTwilioStatus]  = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [copied,        setCopied]        = useState(false);



  // ── Meta state ──────────────────────────────────────────────────────
  const [metaForm, setMetaForm] = useState({
    phoneNumberId: '', accessToken: '', wabaId: ''
  })
  const [metaLoading, setMetaLoading] = useState(false)
  const [metaStatus, setMetaStatus]   = useState(null)
  const [metaCopied, setMetaCopied]   = useState(false)



  useEffect(() => {
    fetchBusinessSettings();
    fetchTwilioStatus();
    fetchMetaStatus()  // ← yeh add karo
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // FETCH
  // ─────────────────────────────────────────────────────────────────────────

  const fetchBusinessSettings = async () => {
    try {
      const { data } = await api.get('/business');
      const biz = data.business;
      if (biz) {
        setBusiness(biz);
        setFormData({ name: biz.name || '', category: biz.category || '' });
      }
    } catch (err) {
      console.error('Failed to load business settings:', err);
    }
  };

  const fetchTwilioStatus = async () => {
    try {
      setStatusLoading(true);
      const { data } = await api.get('/whatsapp/connection-status');
      setTwilioStatus(data);



      // ✅ ADD KARO YEH
    const webhookRes = await api.get('/whatsapp/webhook-url');
    setWebhookUrl(webhookRes.data.webhookUrl);


    } catch (err) {
      console.error('Failed to load Twilio status:', err);
    } finally {
      setStatusLoading(false);
    }
  };



  const fetchMetaStatus = async () => {
  try {
    const { data } = await api.get('/whatsapp/meta-connection-status')
    setMetaStatus(data)
  } catch (err) {
    console.error('Failed to load Meta status:', err)
  }
}

const handleSaveMeta = async () => {
  if (!metaForm.phoneNumberId?.trim()) { toast.error('Phone Number ID required'); return }
  if (!metaForm.accessToken?.trim())   { toast.error('Access Token required'); return }
  if (!metaForm.wabaId?.trim())        { toast.error('WABA ID required'); return }

  setMetaLoading(true)
  try {
    await api.post('/whatsapp/save-meta-credentials', {
      phoneNumberId: metaForm.phoneNumberId.trim(),
      accessToken:   metaForm.accessToken.trim(),
      wabaId:        metaForm.wabaId.trim(),
    })
    toast.success('Meta WhatsApp connected! 🎉')
    setMetaForm({ phoneNumberId: '', accessToken: '', wabaId: '' })
    fetchMetaStatus()
  } catch (err) {
    toast.error(err.response?.data?.error || 'Failed to connect Meta')
  } finally {
    setMetaLoading(false)
  }
}

const handleMetaDisconnect = async () => {
  if (!window.confirm('Disconnect Meta WhatsApp?')) return
  setMetaLoading(true)
  try {
    await api.delete('/whatsapp/meta-disconnect')
    toast.success('Meta disconnected')
    setMetaStatus(null)
  } catch {
    toast.error('Failed to disconnect')
  } finally {
    setMetaLoading(false)
  }
}



  // ─────────────────────────────────────────────────────────────────────────
  // SAVE BUSINESS
  // ─────────────────────────────────────────────────────────────────────────

  const handleSaveBusiness = async () => {
    if (!formData.name?.trim()) { toast.error('Business name is required'); return; }

    setBizLoading(true);
    try {
      await api.put('/business', {
        name:     formData.name,
        category: formData.category,
      });
      toast.success('Business details saved!');
      fetchBusinessSettings();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save business details');
    } finally {
      setBizLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // SAVE TWILIO CREDENTIALS
  // ─────────────────────────────────────────────────────────────────────────

  const handleSaveTwilio = async () => {
    if (!twilioForm.accountSid?.trim())     { toast.error('Account SID is required'); return; }
    if (!twilioForm.authToken?.trim())      { toast.error('Auth Token is required'); return; }
    if (!twilioForm.whatsappNumber?.trim()) { toast.error('WhatsApp number is required'); return; }

    setTwilioLoading(true);
    try {
      await api.post('/whatsapp/save-credentials', {
        accountSid:     twilioForm.accountSid.trim(),
        authToken:      twilioForm.authToken.trim(),
        whatsappNumber: twilioForm.whatsappNumber.trim(),
      });

      toast.success('Twilio connected successfully! 🎉');
      setTwilioForm({ accountSid: '', authToken: '', whatsappNumber: '' });
      fetchTwilioStatus();

    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to connect Twilio. Check your credentials.');
    } finally {
      setTwilioLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // DISCONNECT TWILIO
  // ─────────────────────────────────────────────────────────────────────────

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Twilio? This will stop all WhatsApp automation.')) return;

    setTwilioLoading(true);
    try {
      await api.delete('/whatsapp/disconnect');
      toast.success('Twilio disconnected');
      setTwilioStatus(null);
    } catch {
      toast.error('Failed to disconnect Twilio');
    } finally {
      setTwilioLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // COPY WEBHOOK URL
  // ─────────────────────────────────────────────────────────────────────────

  // const webhookUrl = `${window.location.origin.replace('3000', '3001')}/webhooks/

  const [webhookUrl, setWebhookUrl] = useState('');

  const handleCopyWebhook = () => {
    navigator.clipboard.writeText(webhookUrl);
    setCopied(true);
    toast.success('Webhook URL copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const isConnected = twilioStatus?.connected;

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-8 max-w-3xl" data-testid="settings-page">

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <motion.div {...fadeUp(0)}>
        <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Settings</h1>
        <p className="mt-1 text-sm text-gray-400">Manage your account and business configuration.</p>
      </motion.div>

      {/* ── Account Information ───────────────────────────────────────────── */}
      <motion.div {...fadeUp(0.06)}>
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-emerald-100 flex items-center justify-center">
              <User className="w-4 h-4 text-emerald-600" />
            </div>
            <h2 className="text-base font-bold text-gray-900">Account Information</h2>
          </div>
          <div className="p-6 space-y-4">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-gray-700">Email</Label>
              <Input
                value={user?.email || ''}
                disabled
                className="h-10 rounded-xl border-gray-200 bg-gray-50 text-gray-600 cursor-not-allowed"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-gray-700">Business Name</Label>
              <Input
                value={user?.business_name || ''}
                disabled
                className="h-10 rounded-xl border-gray-200 bg-gray-50 text-gray-600 cursor-not-allowed"
              />
            </div>
            <div className="flex items-center gap-2 p-3 rounded-xl bg-blue-50 border border-blue-100">
              <Shield className="w-4 h-4 text-blue-500 flex-shrink-0" />
              <p className="text-xs text-blue-700">
                Account information is protected and cannot be changed from this page.
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* ── Business Details ──────────────────────────────────────────────── */}
      <motion.div {...fadeUp(0.12)}>
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-emerald-100 flex items-center justify-center">
              <Building2 className="w-4 h-4 text-emerald-600" />
            </div>
            <h2 className="text-base font-bold text-gray-900">Business Details</h2>
          </div>
          <div className="p-6 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="business-name" className="text-sm font-medium text-gray-700">
                Business Name <span className="text-rose-400">*</span>
              </Label>
              <Input
                id="business-name"
                value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                placeholder="My Awesome Store"
                data-testid="settings-business-name"
                className="h-10 rounded-xl border-gray-200 focus:border-emerald-400 focus:ring-emerald-400 text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="category" className="text-sm font-medium text-gray-700">Category</Label>
              <Input
                id="category"
                value={formData.category}
                onChange={e => setFormData({ ...formData, category: e.target.value })}
                placeholder="E-commerce, Services, etc."
                data-testid="settings-category"
                className="h-10 rounded-xl border-gray-200 focus:border-emerald-400 focus:ring-emerald-400 text-sm"
              />
            </div>
            <button
              onClick={handleSaveBusiness}
              disabled={bizLoading}
              data-testid="save-business-button"
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white text-sm font-semibold shadow-lg shadow-emerald-200/60 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
            >
              {bizLoading
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
                : <><Save className="w-4 h-4" /> Save Business Details</>
              }
            </button>
          </div>
        </div>
      </motion.div>

      {/* ── Twilio WhatsApp Connection ────────────────────────────────────── */}
      <motion.div {...fadeUp(0.18)}>
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">

          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-emerald-100 flex items-center justify-center">
                <Phone className="w-4 h-4 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-base font-bold text-gray-900">Twilio WhatsApp</h2>
                <p className="text-xs text-gray-400 mt-0.5">Connect your own Twilio account to enable automation.</p>
              </div>
            </div>

            {/* Status badge */}
            {statusLoading ? (
              <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
            ) : (
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${
                isConnected ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'
              }`}>
                <span className={`w-1.5 h-1.5 rounded-full ${isConnected ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                {isConnected ? 'Connected' : 'Not Connected'}
              </div>
            )}
          </div>

          <div className="p-6 space-y-5">

            {/* ── CONNECTED STATE ──────────────────────────────────────────── */}
            {isConnected ? (
              <div className="space-y-4">

                {/* Connected info card */}
                <div className="flex items-center gap-3 p-4 rounded-xl bg-emerald-50 border border-emerald-200">
                  <div className="relative w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <Wifi className="w-5 h-5 text-emerald-600" />
                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-500 rounded-full border-2 border-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-emerald-700">WhatsApp Active</p>
                    <p className="text-xs text-emerald-600 font-mono mt-0.5">
                      +{twilioStatus?.whatsappNumber}
                    </p>
                    {twilioStatus?.accountSid && (
                      <p className="text-xs text-emerald-500 mt-0.5">
                        Account: {twilioStatus.accountSid}
                      </p>
                    )}
                  </div>
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                </div>

                {/* Active info */}
                <div className="p-4 bg-emerald-50 border border-emerald-100 rounded-xl flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <Zap className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-emerald-700">Automation is Active</p>
                    <p className="text-xs text-emerald-600 mt-0.5 leading-relaxed">
                      Your bot is ready to receive and reply to WhatsApp messages automatically.
                    </p>
                  </div>
                </div>

                {/* Webhook URL */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    Webhook URL
                    <span className="ml-2 text-xs text-gray-400 font-normal">— paste in Twilio Console</span>
                  </Label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 px-3 py-2.5 rounded-xl bg-gray-50 border border-gray-200 font-mono text-xs text-gray-600 truncate">
                      {webhookUrl}
                    </div>
                    <button
                      onClick={handleCopyWebhook}
                      className="flex-shrink-0 p-2.5 rounded-xl border border-gray-200 hover:border-emerald-400 hover:bg-emerald-50 transition-all"
                    >
                      {copied
                        ? <Check className="w-4 h-4 text-emerald-600" />
                        : <Copy  className="w-4 h-4 text-gray-400" />
                      }
                    </button>
                  </div>
                  <p className="text-xs text-gray-400">
                    Twilio Console → Messaging → WhatsApp → Sandbox → "When a message comes in" field.
                  </p>
                </div>

                {/* Refresh + Disconnect */}
                <div className="flex gap-3">
                  <button
                    onClick={fetchTwilioStatus}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:border-emerald-400 hover:text-emerald-600 transition-all"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Refresh
                  </button>
                  <button
                    onClick={handleDisconnect}
                    disabled={twilioLoading}
                    data-testid="disconnect-whatsapp-button"
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 border-rose-200 text-sm font-semibold text-rose-600 hover:bg-rose-50 hover:border-rose-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {twilioLoading
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <WifiOff  className="w-4 h-4" />
                    }
                    Disconnect Twilio
                  </button>
                </div>
              </div>

            ) : (

              /* ── NOT CONNECTED STATE ───────────────────────────────────── */
              <div className="space-y-5">

                {/* Setup guide */}
                <div className="p-4 bg-blue-50 border border-blue-100 rounded-xl">
                  <p className="text-sm font-semibold text-blue-700 mb-2">How to connect Twilio:</p>
                  <ol className="space-y-1.5 text-xs text-blue-600">
                    {[
                      'Sign up at twilio.com — get your Account SID & Auth Token from Console Dashboard',
                      'Go to Messaging → Try WhatsApp → Join the sandbox',
                      'Enter your credentials below and click Connect',
                      'Copy the Webhook URL and paste it in Twilio Console after connecting',
                    ].map((step, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="flex-shrink-0 w-4 h-4 rounded-full bg-blue-200 text-blue-700 text-xs font-bold flex items-center justify-center mt-0.5">
                          {i + 1}
                        </span>
                        {step}
                      </li>
                    ))}
                  </ol>
                  <a
                    href="https://console.twilio.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 mt-3 text-xs text-blue-600 font-semibold hover:underline"
                  >
                    Open Twilio Console <ExternalLink className="w-3 h-3" />
                  </a>
                </div>

                {/* Account SID */}
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-gray-700">
                    Account SID <span className="text-rose-400">*</span>
                  </Label>
                  <div className="relative">
                    <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <Input
                      value={twilioForm.accountSid}
                      onChange={e => setTwilioForm({ ...twilioForm, accountSid: e.target.value })}
                      placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                      data-testid="twilio-account-sid"
                      disabled={twilioLoading}
                      className="pl-10 h-10 rounded-xl border-gray-200 focus:border-emerald-400 focus:ring-emerald-400 text-sm font-mono"
                    />
                  </div>
                </div>

                {/* Auth Token */}
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-gray-700">
                    Auth Token <span className="text-rose-400">*</span>
                  </Label>
                  <div className="relative">
                    <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <Input
                      type={showToken ? 'text' : 'password'}
                      value={twilioForm.authToken}
                      onChange={e => setTwilioForm({ ...twilioForm, authToken: e.target.value })}
                      placeholder="••••••••••••••••••••••••••••••••"
                      data-testid="twilio-auth-token"
                      disabled={twilioLoading}
                      className="pl-10 pr-10 h-10 rounded-xl border-gray-200 focus:border-emerald-400 focus:ring-emerald-400 text-sm font-mono"
                    />
                    <button
                      type="button"
                      onClick={() => setShowToken(!showToken)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* WhatsApp Number */}
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-gray-700">
                    Twilio WhatsApp Number <span className="text-rose-400">*</span>
                  </Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <Input
                      value={twilioForm.whatsappNumber}
                      onChange={e => setTwilioForm({ ...twilioForm, whatsappNumber: e.target.value })}
                      placeholder="+14155238886"
                      data-testid="twilio-whatsapp-number"
                      disabled={twilioLoading}
                      className="pl-10 h-10 rounded-xl border-gray-200 focus:border-emerald-400 focus:ring-emerald-400 text-sm"
                    />
                  </div>
                  <p className="text-xs text-gray-400">
                    Your Twilio WhatsApp number with country code (e.g. +1 415 523 8886).
                  </p>
                </div>

                {/* Connect button */}
                <button
                  onClick={handleSaveTwilio}
                  disabled={
                    twilioLoading ||
                    !twilioForm.accountSid ||
                    !twilioForm.authToken  ||
                    !twilioForm.whatsappNumber
                  }
                  data-testid="connect-twilio-button"
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white text-sm font-semibold shadow-lg shadow-emerald-200/60 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                >
                  {twilioLoading
                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Connecting…</>
                    : <><Wifi    className="w-4 h-4" /> Connect Twilio</>
                  }
                </button>
              </div>
            )}
          </div>
        </div>
      </motion.div>



      {/* ── Meta WhatsApp Connection ─────────────────────────────────────── */}
<motion.div {...fadeUp(0.22)}>
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">

    {/* Header */}
    <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-green-100 flex items-center justify-center">
          <Phone className="w-4 h-4 text-green-600" />
        </div>
        <div>
          <h2 className="text-base font-bold text-gray-900">Meta WhatsApp (Cloud API)</h2>
          <p className="text-xs text-gray-400 mt-0.5">Connect via Meta Business directly — no Twilio needed.</p>
        </div>
      </div>
      <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${
        metaStatus?.connected ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
      }`}>
        <span className={`w-1.5 h-1.5 rounded-full ${metaStatus?.connected ? 'bg-green-500' : 'bg-gray-400'}`} />
        {metaStatus?.connected ? 'Connected' : 'Not Connected'}
      </div>
    </div>

    <div className="p-6 space-y-5">
      {metaStatus?.connected ? (
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-4 rounded-xl bg-green-50 border border-green-200">
            <Wifi className="w-5 h-5 text-green-600" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-green-700">Meta WhatsApp Active</p>
              <p className="text-xs text-green-600 font-mono mt-0.5">
                ID: {metaStatus?.phoneNumberId}
              </p>
            </div>
            <CheckCircle2 className="w-5 h-5 text-green-500" />
          </div>
          <button
            onClick={handleMetaDisconnect}
            disabled={metaLoading}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 border-rose-200 text-sm font-semibold text-rose-600 hover:bg-rose-50 transition-all disabled:opacity-50"
          >
            {metaLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <WifiOff className="w-4 h-4" />}
            Disconnect Meta
          </button>
        </div>
      ) : (
        <div className="space-y-5">

          {/* Guide */}
          <div className="p-4 bg-blue-50 border border-blue-100 rounded-xl">
            <p className="text-sm font-semibold text-blue-700 mb-2">How to connect Meta:</p>
            <ol className="space-y-1.5 text-xs text-blue-600">
              {[
                'Go to developers.facebook.com → Create App → Add WhatsApp product',
                'Copy your Phone Number ID from API Setup page',
                'Generate a Permanent Access Token',
                'Copy your WhatsApp Business Account ID (WABA ID)',
                'Paste all three below and click Connect',
              ].map((step, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="flex-shrink-0 w-4 h-4 rounded-full bg-blue-200 text-blue-700 text-xs font-bold flex items-center justify-center mt-0.5">
                    {i + 1}
                  </span>
                  {step}
                </li>
              ))}
            </ol>
          </div>

          {/* Phone Number ID */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-gray-700">
              Phone Number ID <span className="text-rose-400">*</span>
            </Label>
            <Input
              value={metaForm.phoneNumberId}
              onChange={e => setMetaForm({ ...metaForm, phoneNumberId: e.target.value })}
              placeholder="1234567890123456"
              disabled={metaLoading}
              className="h-10 rounded-xl border-gray-200 focus:border-green-400 text-sm font-mono"
            />
          </div>

          {/* Access Token */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-gray-700">
              Access Token <span className="text-rose-400">*</span>
            </Label>
            <Input
              type="password"
              value={metaForm.accessToken}
              onChange={e => setMetaForm({ ...metaForm, accessToken: e.target.value })}
              placeholder="EAAxxxxxxxxxxxxxxxxx"
              disabled={metaLoading}
              className="h-10 rounded-xl border-gray-200 focus:border-green-400 text-sm font-mono"
            />
          </div>

          {/* WABA ID */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-gray-700">
              WABA ID <span className="text-rose-400">*</span>
            </Label>
            <Input
              value={metaForm.wabaId}
              onChange={e => setMetaForm({ ...metaForm, wabaId: e.target.value })}
              placeholder="9876543210987654"
              disabled={metaLoading}
              className="h-10 rounded-xl border-gray-200 focus:border-green-400 text-sm font-mono"
            />
          </div>

          {/* Connect Button */}
          <button
            onClick={handleSaveMeta}
            disabled={metaLoading || !metaForm.phoneNumberId || !metaForm.accessToken || !metaForm.wabaId}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white text-sm font-semibold shadow-lg shadow-green-200/60 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
          >
            {metaLoading
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Connecting…</>
              : <><Wifi    className="w-4 h-4" /> Connect Meta WhatsApp</>
            }
          </button>
        </div>
      )}
    </div>
  </div>
</motion.div>



      {/* Help text */}
      <motion.div {...fadeUp(0.24)}>
        <p className="text-center text-xs text-gray-300">
          Changes will take effect immediately after saving.
        </p>
      </motion.div>

    </div>
  );
}
